import { TAppMaster } from '@monorepo/resolvers';
import { TAllViewDescriptors } from '@monorepo/types';
import { TAppMasterDBActions, AppMasterDBActionTypes } from './app-master-db.actions';

export enum AppMasterDBTypes {
  APP_MASTER = 'APP_MASTER',
  APPS = 'APPS',
  MODULES = 'MODULES',
  VIEW_DESCRIPTORS = 'VIEW_DESCRIPTORS',
  BUSINESS_CONTEXT = 'BUSINESS_CONTEXT',
  SUBJECTS = 'SUBJECTS',
}

// "not object" type
type Primitive = string | number | boolean | undefined | null;

/**
 * Replace key K in object T with custom type R
 * Basically T[K] = R
 */
type ReplaceKey<T, K extends string, R> =
  // Is T an object with a K key?
  T extends Record<K, unknown>
    ? // Yup, remove old K key with it's value (Omit<T, K>)
      // and merge the whole object with new object (&)
      // that has K key with replacement value (Record<K, R>)
      Omit<T, K> & Record<K, R>
    : // Nah, not our guy, leave him untouched
      T;

/**
 * Recursively replace type of key K value in object T and it's nested objects with type R
 */
type DeepReplaceHelper<T, K extends string, R, ReplacedT = ReplaceKey<T, K, R>> = {
  // "iterate" keys in ReplacedT
  [Key in keyof ReplacedT]: ReplacedT[Key] extends Primitive // Is value of this key not object?
    ? // It's not an object, nothing to do here
      ReplacedT[Key]
    : // It's an object, but is it an array object?
    ReplacedT[Key] extends unknown[]
    ? // Yes, Replace type of array item, not the array itsel
      DeepReplace<ReplacedT[Key][number], K, R>[]
    : // No, It's not an array, replace it as it is
      DeepReplace<ReplacedT[Key], K, R>;
};

type DeepReplace<T, K extends string, R> = T extends Primitive ? T : DeepReplaceHelper<T, K, R>;

export type TAppMasterDatabase = {
  entities: {
    [AppMasterDBTypes.APP_MASTER]: {
      [appUId: string]: DeepReplace<TAppMaster, 'apps', string[]>;
    };
    [AppMasterDBTypes.APPS]: {
      [appUId: string]: DeepReplace<
        DeepReplace<TAppMaster['apps'][0], 'subjects' | 'bctx', string[]>,
        'modules',
        string[]
      >;
    };
    [AppMasterDBTypes.MODULES]: {
      [moduleUId: string]: DeepReplace<TAppMaster['apps'][0]['appInfo']['modules'][0], 'view_descriptors', string[]>;
    };
    [AppMasterDBTypes.VIEW_DESCRIPTORS]: {
      [moduleUId: string]: TAllViewDescriptors;
    };
    [AppMasterDBTypes.BUSINESS_CONTEXT]: {
      [moduleUId: string]: TAppMaster['apps'][0]['settingInfo']['bctx'][0];
    };
    [AppMasterDBTypes.SUBJECTS]: {
      [moduleUId: string]: TAppMaster['apps'][0]['settingInfo']['subjects'][0];
    };
  };
  result?: string;
  settingsInfo?: {
    appId: string;
    moduleId: string;
    stationId: string;
    businessContextId: string;
    landingViewDescriptorId: string;
    hasSilhouette: boolean;
  };

  originalAppMasterDb: TAppMaster | null;
};

const initialState: TAppMasterDatabase = {
  entities: {
    APP_MASTER: {},
    APPS: {},
    MODULES: {},
    VIEW_DESCRIPTORS: {},
    BUSINESS_CONTEXT: {},
    SUBJECTS: {},
  },
  originalAppMasterDb: null,
};

export function appMasterDBReducer(state = initialState, action: TAppMasterDBActions): TAppMasterDatabase {
  switch (action.type) {
    case AppMasterDBActionTypes.AppMasterDBLoaded:
      return {
        ...action.payload,
      };
    case AppMasterDBActionTypes.LoadAppMasterDBSettings:
      return {
        ...state,
        settingsInfo: {
          ...action.payload,
        },
      };
    case AppMasterDBActionTypes.ChangeStation:
      return {
        ...state,
        ...(state.settingsInfo && {
          settingsInfo: {
            ...state.settingsInfo,
            stationId: action.payload,
          },
        }),
      };
    default:
      return state;
  }
}
